import {
    Observable, throwError
} from "rxjs";
import {
    catchError, map
} from "rxjs/operators";

import {
    Injectable, inject
} from "@angular/core";
import {
    HttpClient
} from "@angular/common/http";

import { FlyerVersion } from "@shopliftr/common-js/admin";
import { ISearchSort } from "@shopliftr/common-js/data";

import { Results } from "@shopliftr/common-js/shared";
import { IInternalApiConfig } from "../../../interfaces/internal-api-config";
import { INTERNAL_API_APP_CONFIG } from "../../../constants/iapi-app-tokens";
import { COMMON_ERROR_HANDLER } from "../../../../exceptions/constants/exception-constants";

@Injectable({ providedIn: "root" })
export class FlyerVersionRequestService {
    private readonly _httpClient = inject(HttpClient);
    private readonly _appConfig = inject<IInternalApiConfig>(INTERNAL_API_APP_CONFIG);
    private readonly _errorHandler = inject(COMMON_ERROR_HANDLER);

    private readonly _flyerUrl: string;

    constructor() {

        this._flyerUrl = `${this._appConfig.apiUrl}/v1/flyer-version`;
    }

    createFlyerVersion$(flyerVersion: FlyerVersion): Observable<FlyerVersion> {

        return this._httpClient.post(this._flyerUrl, flyerVersion.serialize()).pipe(
            map((response: unknown): FlyerVersion => {

                return FlyerVersion.deserialize(response);
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    deleteFlyerVersion$(flyerVersionId: string, version: number): Observable<boolean> {

        const url = `${this._flyerUrl}/${flyerVersionId}`;

        return this._httpClient.delete(url, { body: { esVersion: version } }).pipe(
            map(() => {

                return true;
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    getFlyerVersion$(flyerVersionId: string): Observable<FlyerVersion> {

        const url = `${this._flyerUrl}/${flyerVersionId}`;

        return this._httpClient.get(url).pipe(
            map((response: unknown): FlyerVersion => {

                return FlyerVersion.deserialize(response);
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    createRootFlyerVersionFromNode$(rootId: string, nodeId: string, rootVersion: number): Observable<boolean> {

        const url = `${this._flyerUrl}/${rootId}/node/${nodeId}/make-root-flyer-version`;

        return this._httpClient.put(url, { esVersion: rootVersion }).pipe(
            map((): boolean => {

                return true;
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    moveFlyerVersionNode$(
        rootId: string,
        nodeId: string,
        sourceVersion: number,
        targetRootId: string,
        targetVersion: number,
        targetNodeId?: string
    ): Observable<boolean> {

        const url = `${this._flyerUrl}/${rootId}/node/${nodeId}/move`;
        const payload = {
            targetRootFlyerVersionId: targetRootId,
            targetFlyerVersionNodeId: targetNodeId,
            sourceEsVersion: sourceVersion,
            targetEsVersion: targetVersion
        };

        return this._httpClient.put(url, payload).pipe(
            map((): boolean => {

                return true;
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    convertFlyerVersionToChildNode$(rootId: string, sourceVersion: number,
        targetRootId: string, targetVersion: number, targetNodeId?: string): Observable<boolean> {

        const url = `${this._flyerUrl}/${rootId}/convert-to-child`;
        const payload = {
            targetRootFlyerVersionId: targetRootId,
            targetFlyerVersionNodeId: targetNodeId,
            sourceEsVersion: sourceVersion,
            targetEsVersion: targetVersion
        };

        return this._httpClient.put(url, payload).pipe(
            map((): boolean => {

                return true;
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    getAllByTeam$(teamId: string): Observable<Array<FlyerVersion>> {

        return this.search$(undefined, undefined, undefined, teamId);
    }

    /**
     * Get all flyer versions with given filter options
     */
    getAll$(
        chainId?: string,
        marketAreaIds?: Array<string>,
        teamId?: string,
        sort?: Array<ISearchSort>
    ): Observable<Array<FlyerVersion>> {
        const payload: any = {
            chainId: chainId,
            marketAreaIds: marketAreaIds,
            teamId: teamId,
            size: 10000,
            from: 0
        };

        // Add sort parameters if provided
        if (sort && sort.length > 0) {
            payload.sort = sort[0].field;
            payload.sortDir = sort[0].direction;
        }

        const url = `${this._flyerUrl}/search`;

        return this._httpClient.post(url, payload).pipe(
            map((response: Results<FlyerVersion>): Array<FlyerVersion> => {
                return FlyerVersion.deserializeArray(response.results);
            }),
            catchError((error: unknown) => {
                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    search$(chainId?: string, term?: string, marketAreaIds?: Array<string>, teamId?: string): Observable<Array<FlyerVersion>> {

        const payload: any = {
            chainId: chainId,
            marketAreaIds: marketAreaIds,
            term: term,
            teamId: teamId,
            size: 1000,
            from: 0
        };

        const url = `${this._flyerUrl}/search`;

        return this._httpClient.post(url, payload).pipe(
            map((response: Results<FlyerVersion>): Array<FlyerVersion> => {

                return FlyerVersion.deserializeArray(response.results);
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }

    updateFlyerVersion$(flyerVersion: FlyerVersion): Observable<FlyerVersion> {

        const url = `${this._flyerUrl}/${flyerVersion.id}`;

        return this._httpClient.put(url, flyerVersion.serialize()).pipe(
            map((response: unknown) => {

                return FlyerVersion.deserialize(response);
            }),
            catchError((error: unknown) => {

                return throwError(() => this._errorHandler.handleServiceError(error));
            })
        );
    }
}
